import { PortalMode } from '@servicetitan/cp-ui';
import {
    BodyText,
    Button,
    ButtonProps,
    Card,
    Icon,
    IconPropsStrict,
    Stack,
} from '@servicetitan/design-system';
import classNames from 'classnames';
import { FC, ReactNode } from 'react';
import { useCallUsProps } from '../call-us';
import * as Styles from './icon-card.module.less';

export const IconCard: FC<{
    className?: string;
    iconClassName?: string;
    wide?: boolean;
    icon: IconPropsStrict['name'];
    actionButton?: ButtonProps;
    mode: PortalMode;
    children?: ReactNode;
    dataTrackingComponent?: string;
}> = ({
    actionButton,
    children,
    className,
    dataTrackingComponent,
    icon,
    iconClassName,
    mode,
    wide,
}) => {
    const tabletMode = mode.tablet || (wide && mode.desktop);

    return (
        <Stack
            className={classNames('bg-white border border-radius-2 p-2', className)}
            direction={tabletMode ? 'row' : 'column'}
            alignItems={tabletMode ? 'flex-start' : undefined}
            data-tracking-component={dataTrackingComponent}
        >
            <Stack
                className={classNames({
                    'flex-grow-1 flex-basis-0 min-w-0': tabletMode,
                    'm-b-2': actionButton && !tabletMode,
                    'm-r-2': actionButton && tabletMode,
                })}
            >
                <Stack.Item className="p-r-1 m-r-1 m-t-half">
                    <Icon className={iconClassName} name={icon} size={24} />
                </Stack.Item>
                <Stack.Item fill>{children}</Stack.Item>
            </Stack>
            {!!actionButton && (
                <Button width={mode.mobile ? 'full' : undefined} {...actionButton} />
            )}
        </Stack>
    );
};

export const EmptyIconCard: FC<{
    className?: string;
    icon: IconPropsStrict['name'];
    filtered: boolean;
    title: string;
    description: string;
    mode: PortalMode;
    resetFilters(): void;
}> = ({ className, description, filtered, icon, mode, resetFilters, title }) => {
    const callUsProps = useCallUsProps();

    return (
        <IconCard
            className={className}
            icon={filtered ? 'not_interested' : icon}
            iconClassName={filtered ? 'c-red-500' : 'c-blue-400'}
            mode={mode}
            wide
            actionButton={
                filtered
                    ? {
                          iconName: 'refresh',
                          onClick: resetFilters,
                          children: 'Reset filters',
                      }
                    : callUsProps
            }
        >
            <BodyText className="m-b-half" size="large" bold>
                {title}
            </BodyText>
            <BodyText size="small" subdued>
                {filtered ? 'Please change filter criteria.' : description}
            </BodyText>
        </IconCard>
    );
};

export const SectionIconCard: FC<{
    className?: string;
    title: string;
    description?: string;
    icon: IconPropsStrict['name'];
    mode: PortalMode;
    children?: ReactNode;
}> = ({ children, className, description, icon, title }) => {
    return (
        <Card className={className} padding="thin">
            <Card.Section className="d-f align-items-center">
                <Stack
                    className={classNames(
                        'bg-neutral-30 border-radius-2 m-r-1',
                        Styles.sectionedIcon
                    )}
                    justifyContent="center"
                    alignItems="center"
                >
                    <Icon name={icon} className="c-blue-500" size={20} />
                </Stack>
                <Stack.Item fill>
                    <BodyText bold>{title}</BodyText>
                    {!!description && (
                        <BodyText className="m-t-half" size="small" subdued>
                            {description}
                        </BodyText>
                    )}
                </Stack.Item>
            </Card.Section>
            <Card.Section>{children}</Card.Section>
        </Card>
    );
};
